import { View } from 'react-native'
import { Input } from '@/src/common/components/Input'
import { DropdownComponent } from '@/src/common/components/Dropdown'
import {
  type Address,
  type AddressInputsProps,
  stateDropdownItems,
} from './AutoCompleteAddressCommon'
import Ionicons from '@expo/vector-icons/Ionicons'
import type { SardineId } from '@/src/services/Sardine/SardineId'
import { mesaColorNames } from '@/src/common/theme'

export interface FullAddressInputsProps extends AddressInputsProps {
  sardineIds?: {
    street1: SardineId
    street2: SardineId
    city: SardineId
    state: SardineId
    zipCode: SardineId
  }
}

const FullAddressInputs = (props: FullAddressInputsProps) => {
  const handleAddressChange = <T extends keyof Address>(field: T, value: Address[T]) => {
    // HACK: We have no way to detect if the "clear" button is pressed on the text input,
    // so let's just clear out the entire structure if street1 is erased
    if (field === 'street1' && value === '') {
      props.onChange(undefined)
      return
    }

    props.onChange({
      ...props.address,
      [field]: value,
    })
  }

  const validationErrorsToShow = props.showValidationErrors ? props.validationErrors : undefined

  return (
    <View style={{ gap: 10 }}>
      <Input
        placeholder="Street 1"
        value={props.address.street1}
        onChangeText={(value) => handleAddressChange('street1', value)}
        leftIcon={
          <Ionicons
            size={24}
            name="location-outline"
            color={mesaColorNames['semantic/fg/subtle #898D8F']}
          />
        }
        segmentAction="User Inputs Street Line 1"
        clearButtonMode="always"
        style={styles.inputContainer}
        info={{ error: validationErrorsToShow?.street1 }}
        testID={props.testIDPrefix ? `${props.testIDPrefix}#Street1` : undefined}
        sardineId={props.sardineIds?.street1}
      />
      <Input
        placeholder="Street 2"
        value={props.address.street2}
        onChangeText={(value) => handleAddressChange('street2', value)}
        segmentAction="User Inputs Street Line 2"
        containerStyle={styles.inputContainer}
        info={{ error: validationErrorsToShow?.street2 }}
        testID={props.testIDPrefix ? `${props.testIDPrefix}#Street2` : undefined}
        sardineId={props.sardineIds?.street2}
      />
      <View style={{ flexDirection: 'row', gap: 10 }}>
        <View style={{ flex: 1 }}>
          <Input
            placeholder="City"
            value={props.address.city}
            onChangeText={(value) => handleAddressChange('city', value)}
            segmentAction="User Inputs City"
            containerStyle={styles.inputContainer}
            info={{ error: validationErrorsToShow?.city }}
            testID={props.testIDPrefix ? `${props.testIDPrefix}#City` : undefined}
            sardineId={props.sardineIds?.city}
          />
        </View>
        <View style={{ flex: 1 }}>
          <DropdownComponent
            value={props.address.state}
            data={stateDropdownItems}
            labelField="label"
            valueField="value"
            onChange={(selectedItem) => handleAddressChange('state', selectedItem.value)}
            segmentAction="User Inputs State"
            containerStyle={styles.inputContainer}
            testID={props.testIDPrefix ? `${props.testIDPrefix}#State` : undefined}
            sardineId={props.sardineIds?.state}
          />
        </View>
      </View>
      <Input
        placeholder="Zip Code"
        value={props.address.zipCode}
        onChangeText={(value) => handleAddressChange('zipCode', value)}
        segmentAction="User Inputs Zip Code"
        containerStyle={styles.inputContainer}
        info={{ error: validationErrorsToShow?.zipCode }}
        testID={props.testIDPrefix ? `${props.testIDPrefix}#ZipCode` : undefined}
        sardineId={props.sardineIds?.zipCode}
      />
    </View>
  )
}

const styles = {
  inputContainer: {
    paddingHorizontal: 0,
  },
}

export default FullAddressInputs
